<template>
  <div class="route-card">
    <div class="route-card__header" @click="toggle">
      <div class="route-card__heading">
        <StatusBadge
          v-if="route.status"
          :label="route.status"
          :active="route.statusValor === FREIGHT_TABLE_STATUS.ATIVA"
        />

        <h3 class="route-card__title">
          {{ route.nome }}: <strong>{{ route.destino }}</strong>
        </h3>

        <p class="route-card__meta">
          <template v-for="(part, index) in metaParts">
            <span v-if="index > 0" :key="`sep-${index}`" class="route-card__separator">
              •
            </span>
            <span :key="index">{{ part }}</span>
          </template>
        </p>
      </div>

      <a-button class="route-card__toggle" type="link">
        <a-icon :type="expanded ? 'up' : 'down'" />
      </a-button>
    </div>

    <div v-if="expanded" class="route-card__body">
      <a-tabs v-model="activeTab" class="route-card__tabs">
        <a-tab-pane key="vigencia" tab="Vigência">
          <FreightTableRouteValidities
            :items="validities"
            :counts="validitiesCounts"
            :loading="validitiesLoading"
            :selected-ids="selectedIds"
            :can-approve="canApprove"
            :can-cancel="canCancel"
            :open-validity-id="openValidityId"
            :can-create="canCreateValidity"
            @create="$emit('new-validity', route)"
            @filter-change="onValiditiesFilterChange"
            @toggle-selection="onToggleSelection"
            @review="onReview"
            @open-details="$emit('open-validity', { route, validity: $event })"
          />
        </a-tab-pane>

        <a-tab-pane key="carga" tab="Carga">
          <FreightTableRouteCargos :items="cargos" :loading="cargosLoading" />
        </a-tab-pane>

        <a-tab-pane key="transporte" tab="Transporte">
          <FreightTableRouteTransports
            :items="transports"
            :loading="transportsLoading"
          />
        </a-tab-pane>

        <a-tab-pane key="caracteristicas" tab="Características">
          <FreightTableRouteCharacteristics
            :operacao="route.operacao"
            :processo="route.processo"
          />
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { Modal } from "ant-design-vue";
import StatusBadge from "@/shared/components/StatusBadge.vue";
import FreightTableRouteValidities from "./FreightTableRouteValidities.vue";
import FreightTableRouteCargos from "./FreightTableRouteCargos.vue";
import FreightTableRouteTransports from "./FreightTableRouteTransports.vue";
import FreightTableRouteCharacteristics from "./FreightTableRouteCharacteristics.vue";
import { FREIGHT_TABLE_STATUS } from "../../constants/freightTableStatus";
import { FREIGHT_VALIDITY_STATUS } from "../../constants/freightValidityStatus";
import { handleApiError } from "@/shared/utils/handleApiError";

export default {
  name: "FreightTableRouteCard",
  components: {
    StatusBadge,
    FreightTableRouteValidities,
    FreightTableRouteCargos,
    FreightTableRouteTransports,
    FreightTableRouteCharacteristics,
  },
  props: {
    freightTableId: {
      type: Number,
      required: true,
    },
    route: {
      type: Object,
      required: true,
    },
    canApprove: {
      type: Boolean,
      default: true,
    },
    canCancel: {
      type: Boolean,
      default: true,
    },
    openValidityId: {
      type: Number,
      default: null,
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
    reloadToken: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      FREIGHT_TABLE_STATUS,
      expanded: false,
      activeTab: "vigencia",
      validities: [],
      validitiesCounts: {},
      validitiesLoading: false,
      validitiesStale: false,
      cargos: [],
      cargosLoading: false,
      cargosRequested: false,
      transports: [],
      transportsLoading: false,
      transportsRequested: false,
      selectedIds: [],
      validitiesFilters: {
        status: FREIGHT_VALIDITY_STATUS.PENDENTE,
        search: "",
      },
    };
  },
  computed: {
    canCreateValidity() {
      return (
        this.canCreate && this.route.statusValor === FREIGHT_TABLE_STATUS.ATIVA
      );
    },
    metaParts() {
      const paradas = this.route.paradas || [];
      if (paradas.length) {
        return [`${paradas.length} paradas`, paradas.join(" - ")];
      }

      return this.route.meta || [];
    },
  },
  watch: {
    reloadToken() {
      this.selectedIds = [];
      if (this.expanded) {
        this.loadValidities();
      } else {
        this.validitiesStale = true;
      }
    },
    activeTab(tab) {
      if (tab === "carga" && !this.cargosRequested) this.loadCargos();
      if (tab === "transporte" && !this.transportsRequested) {
        this.loadTransports();
      }
    },
  },
  methods: {
    ...mapActions("freightTables", [
      "fetchRouteValidities",
      "fetchRouteCargos",
      "fetchRouteTransports",
      "updateValidityStatus",
    ]),
    async loadTransports() {
      this.transportsRequested = true;
      this.transportsLoading = true;

      try {
        const result = await this.fetchRouteTransports({
          freightTableId: this.freightTableId,
          routeId: this.route.routeId,
        });
        this.transports = result.items;
      } catch (error) {
        this.transportsRequested = false;
        handleApiError(
          this,
          error,
          "Não foi possível carregar os meios de transporte",
        );
      } finally {
        this.transportsLoading = false;
      }
    },
    async loadCargos() {
      this.cargosRequested = true;
      this.cargosLoading = true;

      try {
        const result = await this.fetchRouteCargos({
          freightTableId: this.freightTableId,
          routeId: this.route.routeId,
        });
        this.cargos = result.items;
      } catch (error) {
        this.cargosRequested = false;
        handleApiError(this, error, "Não foi possível carregar as cargas");
      } finally {
        this.cargosLoading = false;
      }
    },
    toggle() {
      this.expanded = !this.expanded;
      if (this.expanded && (this.validitiesStale || !this.validities.length)) {
        this.loadValidities();
      }
    },
    async loadValidities() {
      this.validitiesLoading = true;
      this.validitiesStale = false;

      try {
        const result = await this.fetchRouteValidities({
          freightTableId: this.freightTableId,
          routeId: this.route.routeId,
          status: this.validitiesFilters.status,
          search: this.validitiesFilters.search,
        });
        this.validities = result.items;
        this.validitiesCounts = result.counts || {};
      } catch (error) {
        handleApiError(this, error, "Não foi possível carregar as vigências");
      } finally {
        this.validitiesLoading = false;
      }
    },
    onValiditiesFilterChange(filters) {
      this.validitiesFilters = { ...this.validitiesFilters, ...filters };
      this.selectedIds = [];
      this.loadValidities();
    },
    onToggleSelection(validityId) {
      const index = this.selectedIds.indexOf(validityId);
      if (index === -1) {
        this.selectedIds = this.selectedIds.concat([validityId]);
        return;
      }
      this.selectedIds = this.selectedIds.filter((id) => id !== validityId);
    },
    onReview({ validityIds, approve }) {
      Modal.confirm({
        title: approve ? "Aprovar?" : "Cancelar?",
        content: approve
          ? "Você deseja aprovar essa(s) vigência(s)?"
          : "Você deseja cancelar essa(s) vigência(s)?",
        okText: "Sim",
        cancelText: "Não",
        class: "freight-tables-confirm",
        onOk: () => this.reviewValidities({ validityIds, approve }),
      });
    },
    async reviewValidities({ validityIds, approve }) {
      try {
        await this.updateValidityStatus({
          freightTableId: this.freightTableId,
          routeId: this.route.routeId,
          validityIds,
          status: approve
            ? FREIGHT_VALIDITY_STATUS.APROVADA
            : FREIGHT_VALIDITY_STATUS.CANCELADA,
        });
        this.selectedIds = [];
        await this.loadValidities();
      } catch (error) {
        handleApiError(
          this,
          error,
          approve
            ? "Não foi possível aprovar as vigências"
            : "Não foi possível cancelar as vigências",
        );
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.route-card {
  border: 1px solid @color-bg-subtle;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px;
    cursor: pointer;
  }

  &__title {
    font-family: @font-primary;
    font-size: 16px;
    font-weight: 400;
    color: @color-text-dark;
    margin: 8px 0 4px 0;
  }

  &__meta {
    font-size: 14px;
    color: @color-text-light;
    margin: 0;
  }

  &__separator {
    margin: 0 8px;
  }

  &__toggle {
    color: @color-text-medium;
  }

  &__body {
    background-color: @color-bg-subtle;
    padding: 16px 20px 20px 20px;
  }

  &__tabs {
    /deep/ .ant-tabs-nav .ant-tabs-tab {
      font-size: 14px;
      color: @color-text-medium;
      padding: 8px 0;
      margin-right: 32px;
    }

    /deep/ .ant-tabs-nav .ant-tabs-tab-active {
      color: @color-primary;
      font-weight: 500;
    }

    /deep/ .ant-tabs-ink-bar {
      background-color: @color-primary;
      height: 2px;
    }

    /deep/ .ant-tabs-bar {
      border-bottom-color: rgba(0, 0, 0, 0.06);
    }
  }
}
</style>
